import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Eye, Settings2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { useDeleteSmsWebhook, useSmsWebhookList } from '../api';
import { JsonViewerDialog } from '../components/JsonViewerDialog';
import type { SmsWebhookRow } from '../types';

interface ViewModal {
  title: string;
  content: string;
}

export function SmsWebhookListPage() {
  const navigate = useNavigate();
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const query = useSmsWebhookList();
  const del = useDeleteSmsWebhook();
  const rows = query.data ?? [];

  const [pendingDelete, setPendingDelete] = useState<SmsWebhookRow | null>(null);
  const [modal, setModal] = useState<ViewModal | null>(null);

  const columns = useMemo<ColumnDef<SmsWebhookRow>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.id}
          </span>
        ),
      },
      {
        accessorKey: 'webhookName',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to={`/update-sms-webhooks/${row.original.id}`}
            className="font-medium text-foreground hover:underline"
          >
            {row.original.webhookName}
          </Link>
        ),
      },
      {
        accessorKey: 'requestType',
        header: 'Method',
        size: 100,
        cell: ({ row }) => (
          <Badge variant="outline" className="font-mono">
            {row.original.requestType || '—'}
          </Badge>
        ),
      },
      {
        accessorKey: 'url',
        header: 'URL',
        cell: ({ row }) => (
          <span className="break-all font-mono text-xs">{row.original.url}</span>
        ),
      },
      {
        id: 'headerView',
        header: 'Headers',
        size: 90,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.header ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setModal({ title: 'Headers', content: row.original.header ?? '' })
              }
            >
              <Eye className="h-4 w-4" />
              View
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'timestamp',
        header: 'Request Date',
        size: 170,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.timestamp ?? '—'}
          </span>
        ),
      },
      {
        id: 'bodyView',
        header: 'Body',
        size: 90,
        enableSorting: false,
        cell: ({ row }) => {
          const content =
            row.original.requestType === 'POST'
              ? row.original.payload
              : row.original.smsText;
          return content ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setModal({
                  title:
                    row.original.requestType === 'POST' ? 'Payload' : 'SMS text',
                  content: content ?? '',
                })
              }
            >
              <Eye className="h-4 w-4" />
              View
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          );
        },
      },
      // Edit + Delete are gated to non-SADMIN roles — super admins and
      // sub-super-admins can browse SMS webhooks but cannot mutate them
      // (they're customer-owned configs). Drop the column entirely so the
      // table doesn't leave a stranded "Settings" header.
      ...(isSubOrSuper
        ? []
        : [
            {
              id: 'actions',
              header: 'Settings',
              size: 90,
              enableHiding: false,
              enableSorting: false,
              cell: ({ row }: { row: { original: SmsWebhookRow } }) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      aria-label="Row actions"
                    >
                      <Settings2 className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel>
                      {row.original.webhookName}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() =>
                        navigate(`/update-sms-webhooks/${row.original.id}`)
                      }
                    >
                      <Pencil className="h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setPendingDelete(row.original)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            } as ColumnDef<SmsWebhookRow>,
          ]),
    ],
    [navigate, isSubOrSuper],
  );

  const confirmDelete = () => {
    if (!pendingDelete) return;
    del.mutate(pendingDelete.id, {
      onSuccess: (res) => {
        toast.success(res?.message ?? 'SMS webhook deleted');
        setPendingDelete(null);
      },
      onError: () => toast.error('Could not delete the webhook.'),
    });
  };

  return (
    <FillPage>
      <PageHeader
        title="SMS Webhooks"
        description="Outbound HTTP calls used to push SMS via your gateway."
        actions={
          !isSubOrSuper && (
            <Button asChild>
              <Link to="/add-sms-webhooks">
                <Plus className="h-4 w-4" />
                Add SMS webhook
              </Link>
            </Button>
          )
        }
      />

      <DataTable<SmsWebhookRow, unknown>
        tableId="webhooks:sms:list:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search SMS webhooks…"
        emptyTitle="No SMS webhooks yet"
        emptyDescription="Add a webhook to wire up an SMS gateway."
        getRowId={(row) => String(row.id)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename="sms-webhooks.csv"
        pageSize={10}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Are you sure you want to delete?"
        description={
          <>
            SMS webhook{' '}
            <span className="font-medium text-foreground">{pendingDelete?.webhookName}</span>{' '}
            will be permanently deleted.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={del.isPending}
        onConfirm={confirmDelete}
      />

      <JsonViewerDialog
        open={modal !== null}
        onOpenChange={(o) => !o && setModal(null)}
        title={modal?.title ?? ''}
        content={modal?.content ?? ''}
      />
    </FillPage>
  );
}
